import { defineStore } from 'pinia'
import { User, Configuration, Strategy, BuybackStrategy, Extra } from '@/types'
import { useStorage } from '@vueuse/core'
import { forgetRememberedFilters } from '@/composables/useFilterState'
import { i18n } from '@/i18n'

export const useUserStore = defineStore('user', {
    state: () => ({
        user: useStorage('user', null as string | null),
    }),

    getters: {
        check(): boolean {
            return this.profile !== null
        },

        profile(): User | null {
            if (this.user === null) {
                return null
            }
            const user = JSON.parse(this.user) as User
            i18n.global.locale.value = user.language || 'it-IT'

            return user
        },

        configuration(): Configuration | null {
            if (this.profile === null) {
                return null
            }

            return this.profile.configuration
        },

        extra(): Extra | null {
            if (this.profile === null) {
                return null
            }

            return this.profile.extra
        },

        strategies(): Array<Strategy> {
            if (this.profile === null) {
                return []
            }

            return this.profile.strategies
        },

        buybackStrategies(): Array<BuybackStrategy> {
            if (this.profile === null) {
                return []
            }

            return this.profile.buyback_strategies
        },

        /**
         * Whether a country label is needed to tell the rows of a price table apart.
         *
         * The union across both sides, because the prices page merges them (ADR-020) and
         * a row's country is a fact about the row rather than about a declared side. This
         * used to branch on `catalog_type`, which sent everything that was not exactly
         * 'resell' down the buyback branch — including the null an account carries when
         * its catalog holds no price at all. A resell-only account reading its (empty)
         * buyback countries answered "single country" and dropped the label, leaving one
         * indistinguishable row per country.
         */
        isMultiCountry(): boolean {
            if (this.extra === null) {
                return true
            }

            return (
                new Set([
                    ...this.extra.resell_countries,
                    ...this.extra.buyback_countries,
                ]).size > 1
            )
        },
    },
    actions: {
        login(user: User) {
            this.user = JSON.stringify(user)
        },

        logout() {
            this.user = null
            forgetRememberedFilters()
        },

        updateStrategies(strategies: Array<Strategy>) {
            const user = this.profile

            if (user === null) {
                return
            }

            user.strategies = strategies
            this.user = JSON.stringify(user)
        },

        updateBuybackStrategies(strategies: Array<BuybackStrategy>) {
            const user = this.profile

            if (user === null) {
                return
            }

            user.buyback_strategies = strategies
            this.user = JSON.stringify(user)
        },
    },
})
